
import { Deferred } from "devextreme/core/utils/deferred";
import { SortDescriptor } from "devextreme/data";
import ArrayStore from "devextreme/data/array_store";
import CustomStore from "devextreme/data/custom_store";
import DataSource, { DataSourceOptions } from "devextreme/data/data_source";
import { useCallback, useEffect, useMemo, useState } from "react";
import authService from '../api-authorization/AuthorizeService';

//interface IProps {
//    apiUrl: string;
//}

//interface IPropsOptions {
//    apiUrl: string;
//}

export interface IServerValidationError {
    title: string;
    status: number;
    errors: any;
}

const useToken = () => {

    const [token, setToken] = useState("");


    const updateToken = useCallback(() => {
        console.log("updating useToken!");
        authService.getAccessToken().then(newToken => {
            newToken && setToken(prev => prev !== newToken ? newToken : prev);
        })
        //newToken && setToken(prev => prev !== newToken ? newToken : prev);
    },[])

    useEffect(() => {
        updateToken();
        const subId = authService.subscribeForTokenChange(updateToken);
        return () => authService.unsubscribeForTokenChange(subId)
    }, [updateToken])

    return token;
}


const useFetchData = () => {

    const fetchData = useCallback(async (url: string, values: any = null, method: "GET" | "PUT" | "POST" | "DELETE" = "GET", respIsText: boolean = false) => {
        const token = await authService.getAccessToken();
        let headers: any = { 'Content-Type': 'application/json' };
        if (token)
            headers["Authorization"] = `Bearer ${token}`;

        return await fetch(url, {
            method,
            headers,
            body: values ? JSON.stringify(values) : null
        })
            .then(async resp => {
                if (resp.status === 401) {
                    //this.setState({ redirectToLogin: true });
                    //history.createLocation(AuthorizeRoute.loginUrl());
                    //AuthorizeRoute.
                    // window.location.href = await AuthorizeRoute.loginUrl();

                    //Should not happen. getAccessToken() will refresh the token if expired. (or throws error)
                    throw Error("Userdef error: NOT LOGGED IN!"); 
                }
                if (resp.status !== 200) {
                    const contentType = resp.headers.get("content-type");
                    

                    if (contentType?.startsWith("text/plain")) {
                        return Promise.reject("Fout: " + await resp.text().catch(() => ""));
                    }

                    const json: IServerValidationError = await resp.json().catch(() => "");
                    let msg = "Er is een fout opgetreden tijdens het verwerken van de gegevens.";
                    if (json && json.errors) {
                        msg = "Er zijn validatiefouten opgetreden";
                        //let prop: string[];
                        for (let propName in json.errors) {
                            msg += "<br />\n" + json.errors[propName].join("; ");
                        }
                        return Promise.reject(msg);
                    } else {
                        return Promise.reject("Fout: " + await resp.text().catch(() => ""));
                    }
                    //throw Error(msg);
                }
                return resp;
            })
            .then(async resp => (method === "DELETE")
                ? await resp.text() === "true" 
                : respIsText ? await resp.text().catch(e => "") : await resp?.json().catch(e => ""));
    }, [])

    return fetchData;
}

const useDataSourceOptions = (apiUrl: string, sort?: SortDescriptor<any> | Array<SortDescriptor<any>>, filter?: any, error?: (error : Error) => void): DataSourceOptions => {

    const fetchData = useFetchData();

    const createDataSourceOptions = useCallback((url: string): DataSourceOptions => {
        return  {
            //sort: "naam",            
            store: new CustomStore({
                key: "id",
                loadMode: 'raw', //loadMode = raw is important for filter and sort processing!
                byKey: async (key) => await fetchData(url + "/" + key),
                load: async (loadOptions) => await fetchData(url), //.then((data: any[]) => (!sort && data) || data.sort((a: any, b: any) => a[sort || ""] < b[sort || ""] ? -1 : 1)),
                insert: async (values) => await fetchData(url, values, "POST"),
                update: async (key, values) => await fetchData(`${url}/${key}`, values, "PUT"),
                remove: async (key) => await fetchData(`${url}/${key}`, "", "DELETE"),
                errorHandler: error
            }), filter, sort
        }
    }, [fetchData, sort, filter, error])

    const options = useMemo(() => createDataSourceOptions(apiUrl), [apiUrl, createDataSourceOptions]);

    return options;

}

const useDataSource = (apiUrl: string, sort?: SortDescriptor<any> | Array<SortDescriptor<any>>, filter?: any, error?: (error: Error) => void): DataSource => {

    const dsOptions = useDataSourceOptions(apiUrl, sort, filter, error);

    const createStore = useCallback((url: string): DataSource =>
        new DataSource(dsOptions)
        , [dsOptions])

    const dgSource = useMemo(() => {
        console.log("create DS", apiUrl);
        return createStore(apiUrl);
    }, [apiUrl, createStore]);
    useEffect(() => () => { dgSource.dispose(); console.log("DS disposed") }, [dgSource]);

    return dgSource;
}

const useListSourceOptions = <T extends { id?: number | string }>(list?: T[], sort?: SortDescriptor<any> | Array<SortDescriptor<any>>, filter?: any): DataSourceOptions => {

    const createDataSourceOptions = useCallback((): DataSourceOptions => ({
        store: new ArrayStore({ data: list, key: 'id' }),
        paginate: true, pageSize: 15,
        sort, filter

    }), [filter, list, sort]);

    const dgSource = useMemo(() => {
        
        return createDataSourceOptions();
    }, [createDataSourceOptions]);

    return dgSource;
}
const useListSource = <T extends { id?: number | string }>(list?: T[], sort?: SortDescriptor<any> | Array<SortDescriptor<any>>, filter?: any) => {

    const listOptions = useListSourceOptions(list, sort, filter);
    const dgSource = useMemo(() => new DataSource(listOptions), [listOptions]);

    useEffect(() => () => { dgSource?.dispose(); console.log("DS disposed") }, [dgSource]);

    return dgSource;
}

const useUrlList = <T extends { id?: number, [x: string]: any }>(apiUrl: string, sortFn?: ((e: T) => any) | string): T[] => {

    const fetcher = useFetchData();
    const [list, setList] = useState<T[]>([]);

    useEffect(() => {
        const sortCompare = (a: T, b: T) => ((typeof sortFn === 'string' ? a[sortFn] : sortFn!(a)) > (typeof sortFn === 'string' ? b[sortFn] : sortFn!(b)) && 1) || -1;
        fetcher(apiUrl).then((data: any[]) => setList((sortFn && (data.sort(sortCompare))) || data))
    }, [fetcher, apiUrl, sortFn])

    return list;
}

const useUrlListSource = <T extends { id?: number, [x: string]: any }>(apiUrl: string, sort?: SortDescriptor<any> | Array<SortDescriptor<any>>, filter?: any): [DataSource?, T[]?] => {
    const list = useUrlList<T>(apiUrl);
    const ds = useListSource<T>(list, sort, filter);
    return [ds, list];
}

export { useFetchData, useDataSourceOptions, useUrlListSource, useListSource, useListSourceOptions, useUrlList, useToken };
export default useDataSource;